/**
 * fsBridge — typed IPC layer over the native-Rust filesystem edge.
 *
 * The Tauri desktop app owns ALL filesystem work (scan / watch / ACL / SMB /
 * LDAP) in native Rust; this module is the single, typed seam the SPA uses to
 * drive it. Every type mirrors the `#[serde(rename_all = "camelCase")]` structs
 * in `apps/desktop/src-tauri/src/fs/` so the wire shape can't drift.
 *
 * Everything here is desktop-only: the connector tile is gated behind
 * `getEnv().isTauri` (see `catalog.ts`), and each call asserts the Tauri
 * runtime so a stray web invocation fails loud instead of silently no-op'ing.
 * Auth/refresh stays single-sourced in the desktop token store — the SPA reads
 * the bearer token and threads it in; this module never touches the vault.
 */

import { invoke } from "@tauri-apps/api/core";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";

import { getEnv } from "@ando/core";

// ── Wire types (mirror src-tauri/src/fs) ────────────────────────────────────

/** Mirrors `fs::ScanConfig`. */
export interface ScanConfig {
  readonly roots: readonly string[];
  /** Lower-cased extensions WITHOUT the dot; empty ⇒ accept all. */
  readonly extensions: readonly string[];
  readonly excludeGlobs: readonly string[];
  /** `0` ⇒ no size cap. */
  readonly maxFileSizeMb: number;
  readonly followSymlinks: boolean;
  readonly connectorId: string;
}

/** Mirrors `fs::SyncConfig` — a scan plus the cloud target + ACL inputs. */
export interface SyncConfig {
  readonly scan: ScanConfig;
  /** Base API URL incl. version prefix, e.g. `http://localhost:8100/api/v1`. */
  readonly apiBase: string;
  /** Desktop OAuth access token (Bearer). */
  readonly token: string;
  /** Connector-level allow-list from the wizard ACL step. */
  readonly allowedUsers: readonly string[];
}

/**
 * Mirrors `fs::source::SmbConfig` (programmatic network-share credentials).
 * NOT camelCase-renamed Rust-side — all fields are single words, so the wire
 * keys are identical. Credentials are threaded per-call and NEVER persisted on
 * the Rust side (they live in the OS keychain via Rust `keyring`, mirroring `LdapConfig`).
 */
export interface SmbConfig {
  /** `host` or `host:port` (default port 445). */
  readonly host: string;
  readonly share: string;
  readonly username: string;
  readonly password: string;
  /** AD/NT domain; omit/empty for a workgroup share. */
  readonly domain?: string | null;
  /** Auth method. Defaults to NTLM Rust-side when omitted. */
  readonly authMode?: "ntlm" | "kerberos";
  /** Kerberos realm, uppercase (e.g. `CORP.ACME.LOCAL`). Kerberos only. */
  readonly realm?: string | null;
  /** KDC address (`host` or `host:port`, default port 88). Kerberos only. */
  readonly kdcAddress?: string | null;
}

/** Mirrors `fs::FileInfo`. */
export interface FileInfo {
  readonly path: string;
  readonly name: string;
  readonly size: number;
  readonly mtime: number;
  readonly extension: string;
  readonly contentHash: string | null;
}

/** Mirrors `fs::ScanSummary`. */
export interface ScanSummary {
  readonly matchedFiles: number;
  readonly skippedFiles: number;
  readonly totalBytes: number;
}

/** Mirrors `fs::SyncSummary`. */
export interface SyncSummary {
  readonly matchedFiles: number;
  readonly uploadedFiles: number;
  readonly failedFiles: number;
  readonly skippedFiles: number;
  /** True when an upload hit 401 — refresh the token and re-invoke `fsSync`. */
  readonly authExpired: boolean;
  /** Cloud job ids for `syncStore.trackJob`. */
  readonly jobIds: readonly string[];
}

/** Mirrors `fs::ScanProgress` (event `fs-scan-progress`). */
export interface ScanProgress {
  readonly connectorId: string;
  readonly scannedDirs: number;
  readonly totalFiles: number;
  readonly matchedFiles: number;
  readonly skippedFiles: number;
  readonly currentPath: string;
}

/** Mirrors `fs::SyncProgress` (event `fs-sync-progress`). */
export interface SyncProgress {
  readonly connectorId: string;
  readonly uploadedFiles: number;
  readonly failedFiles: number;
  readonly currentPath: string;
  readonly docId: string | null;
  readonly jobId: string | null;
}

/** Mirrors `fs::scheduler::NightlySyncTick` (event `fs-nightly-sync-tick`).
 *
 * Fired by the Rust scheduler once per ``nightlyInterval`` seconds (default
 * 24h, aligned to 03:00 UTC). The SPA listener (``useNightlyFsSync``)
 * re-runs ``runFilesystemSync`` against every active filesystem connector
 * so upstream changes get re-uploaded → MinIO → BE
 * ``SpreadsheetSyncScheduler`` re-imports the PG-side tables on its 05:00
 * UTC tick.
 */
export interface NightlySyncTick {
  readonly firedAtUnix: number;
  readonly reason: string;
}

/** Mirrors `mount::TestConnectionResult`. */
export interface TestConnectionResult {
  readonly ok: boolean;
  /** Local mount path when resolved — use as a scan root. */
  readonly mountPath: string | null;
  readonly error: string | null;
}

/** Mirrors `ldap::LdapConfig`. Creds live in the OS keychain (Rust `keyring`); threaded per-call. */
export interface LdapConfig {
  readonly serverUrl: string;
  readonly bindDn: string;
  readonly bindPassword: string;
  readonly baseDn: string;
  readonly groupAttribute?: string;
  readonly mailAttribute?: string;
}

/** Mirrors `ldap::LdapTestResult`. */
export interface LdapTestResult {
  readonly ok: boolean;
  readonly error: string | null;
}

/** Mirrors `watcher::WatchEvent` (event `fs-watch-event`). */
export interface WatchEvent {
  readonly connectorId: string;
  /** Created/modified paths → SPA re-runs `fsSync` (incremental). */
  readonly upserted: readonly string[];
  /** Removed paths → SPA calls `fsDeleteFile` for each. */
  readonly removed: readonly string[];
}

/** Mirrors `writeback::WritebackResult` (return of `fs_write_file_bytes`). */
export interface WritebackResult {
  readonly ok: boolean;
  readonly bytesWritten: number;
  /** True when the destination already had identical bytes (no disk write). */
  readonly skipped: boolean;
}

// ── Event names (mirror src-tauri/src/fs/events.rs) ──────────────────────────

const SCAN_PROGRESS_EVENT = "fs-scan-progress";
const SYNC_PROGRESS_EVENT = "fs-sync-progress";
const TOKEN_EXPIRED_EVENT = "fs-token-expired";
const WATCH_EVENT = "fs-watch-event";
const NIGHTLY_SYNC_TICK_EVENT = "fs-nightly-sync-tick";

// ── Guard ────────────────────────────────────────────────────────────────────

/** The filesystem connector only exists inside the Tauri shell. */
export function isFilesystemConnectorAvailable(): boolean {
  return getEnv().isTauri;
}

function assertTauri(fn: string): void {
  if (!getEnv().isTauri) {
    throw new Error(
      `${fn}: the filesystem connector is desktop-only (Tauri). This code path must be gated behind isFilesystemConnectorAvailable().`,
    );
  }
}

// ── Commands ─────────────────────────────────────────────────────────────────

/** Open the native folder picker; resolves to the absolute path or `null`. */
export async function fsPickFolder(): Promise<string | null> {
  assertTauri("fsPickFolder");
  return invoke<string | null>("fs_pick_folder");
}

/** Walk + classify (skip-unchanged) without uploading — the wizard preview. */
export async function fsScan(config: ScanConfig): Promise<ScanSummary> {
  assertTauri("fsScan");
  return invoke<ScanSummary>("fs_scan", { config });
}

/**
 * Is a scan root on a network filesystem (SMB/NFS/AFP/…)? Drives the adaptive
 * polling cadence in ``useFilesystemAutoSync`` — native watchers don't see
 * remote-originated changes, so network roots are polled more often. Tolerant:
 * resolves to ``false`` (local) off-desktop or on any backend error.
 */
export async function fsIsNetworkPath(path: string): Promise<boolean> {
  if (!isFilesystemConnectorAvailable()) return false;
  try {
    return await invoke<boolean>("fs_is_network_path", { path });
  } catch {
    return false;
  }
}

/** Walk + upload changed files through the cloud ingest contract. */
export async function fsSync(config: SyncConfig): Promise<SyncSummary> {
  assertTauri("fsSync");
  return invoke<SyncSummary>("fs_sync", { config });
}

/**
 * Preview a network share PROGRAMMATICALLY over SMB (no OS mount) — the SMB
 * analogue of `fsScan`. Enumerates (directory listing only, no file reads) and
 * returns counts. `config.roots` are share-relative paths (`""` ⇒ whole share).
 */
export async function fsScanSmb(config: ScanConfig, smb: SmbConfig): Promise<ScanSummary> {
  assertTauri("fsScanSmb");
  return invoke<ScanSummary>("fs_scan_smb", { config, smb });
}

/**
 * Walk + upload a network share PROGRAMMATICALLY over SMB (no OS mount) — the
 * SMB analogue of `fsSync`. Files are keyed by their `smb://host/share/rel`
 * logical path so a NAS file maps to ONE cloud doc shared across users.
 */
export async function fsSyncSmb(config: SyncConfig, smb: SmbConfig): Promise<SyncSummary> {
  assertTauri("fsSyncSmb");
  return invoke<SyncSummary>("fs_sync_smb", { config, smb });
}

/** True when ``path`` exists on disk (file OR directory — unlike
 * ``fsStatFile``, which is file-only by design). Calls the plugin-fs
 * ``exists`` wire command directly via ``invoke``: the plugin is already a
 * direct Cargo dep with ``fs:allow-exists`` whitelisted in the default
 * capability, so this avoids adding the ``@tauri-apps/plugin-fs`` npm
 * package for a single call. Fail-closed: any error ⇒ ``false``. */
export async function fsPathExists(path: string): Promise<boolean> {
  assertTauri("fsPathExists");
  try {
    return await invoke<boolean>("plugin:fs|exists", { path });
  } catch {
    return false;
  }
}

/** Shallow-list a directory via the plugin-fs ``read_dir`` wire command
 * (``fs:allow-read-dir`` is whitelisted). Returns entry names, or ``null``
 * when the read FAILS — which on macOS almost always means a TCC denial
 * (Downloads/Documents/Desktop are gated per-app): the raw Rust scanner hits
 * the same wall silently and reports an empty scan, so THIS is how the UI
 * tells "no permission" apart from "genuinely no compatible files". */
export async function fsTryReadDir(path: string): Promise<readonly string[] | null> {
  assertTauri("fsTryReadDir");
  try {
    const entries = await invoke<ReadonlyArray<{ name?: string }>>("plugin:fs|read_dir", {
      path,
    });
    return entries.map((e) => e.name ?? "");
  } catch {
    return null;
  }
}

/** Stat a single file (optionally hashing). */
export async function fsStatFile(
  path: string,
  computeHash = false,
): Promise<FileInfo | null> {
  assertTauri("fsStatFile");
  return invoke<FileInfo | null>("fs_stat_file", { path, computeHash });
}

/** Remove a file from the cloud index (watcher deletion, Fase 5). */
export async function fsDeleteFile(args: {
  readonly connectorId: string;
  readonly filePath: string;
  readonly apiBase: string;
  readonly token: string;
}): Promise<void> {
  assertTauri("fsDeleteFile");
  await invoke<void>("fs_delete_file", args);
}

// ── Network shares (Fase 3/4 — mount auto-detect) ────────────────────────────

/**
 * Write bytes back to a local/NAS file (BE → desktop writeback channel).
 *
 * Invoked by the WS bridge in response to a ``filesystem_write_back`` frame.
 * The Rust side re-validates ``path`` against ``allowedRoots`` defensively
 * (mirrors the BE check) and short-circuits when the destination already
 * holds identical bytes (idempotency).
 */
export async function fsWriteFileBytes(args: {
  readonly path: string;
  readonly contentBase64: string;
  readonly allowedRoots: readonly string[];
}): Promise<WritebackResult> {
  assertTauri("fsWriteFileBytes");
  return invoke<WritebackResult>("fs_write_file_bytes", {
    path: args.path,
    contentBase64: args.contentBase64,
    allowedRoots: [...args.allowedRoots],
  });
}

/** Resolve `//host/share` to its local OS mount path, or `null` if not mounted. */
export async function fsResolveShare(
  host: string,
  share: string,
): Promise<string | null> {
  assertTauri("fsResolveShare");
  return invoke<string | null>("fs_resolve_share", { host, share });
}

/** Test a network share's reachability (wizard "Test connessione"). */
export async function fsTestConnection(
  host: string,
  share: string,
): Promise<TestConnectionResult> {
  assertTauri("fsTestConnection");
  return invoke<TestConnectionResult>("fs_test_connection", { host, share });
}

// ── Live watch (Fase 5) ──────────────────────────────────────────────────────

/** Start watching `roots` for a connector; emits `fs-watch-event` batches. */
export async function fsStartWatch(
  connectorId: string,
  roots: readonly string[],
): Promise<void> {
  assertTauri("fsStartWatch");
  await invoke<void>("fs_start_watch", { connectorId, roots });
}

/** Stop watching a connector. */
export async function fsStopWatch(connectorId: string): Promise<void> {
  assertTauri("fsStopWatch");
  await invoke<void>("fs_stop_watch", { connectorId });
}

// ── LDAP / AD (Fase 6) ───────────────────────────────────────────────────────

/** Validate LDAP connectivity + bind credentials. */
export async function ldapBindTest(cfg: LdapConfig): Promise<LdapTestResult> {
  assertTauri("ldapBindTest");
  return invoke<LdapTestResult>("ldap_bind_test", { cfg });
}

/** Resolve a user's group CNs (`memberOf`) against the customer directory. */
export async function ldapResolveGroups(
  cfg: LdapConfig,
  email: string,
): Promise<readonly string[]> {
  assertTauri("ldapResolveGroups");
  return invoke<readonly string[]>("ldap_resolve_groups", { cfg, email });
}

// ── Event subscriptions ──────────────────────────────────────────────────────

/** Subscribe to scan progress ticks. Returns an unlisten fn. */
export function onScanProgress(
  cb: (p: ScanProgress) => void,
): Promise<UnlistenFn> {
  assertTauri("onScanProgress");
  return listen<ScanProgress>(SCAN_PROGRESS_EVENT, (e) => cb(e.payload));
}

/** Subscribe to per-file sync progress. Returns an unlisten fn. */
export function onSyncProgress(
  cb: (p: SyncProgress) => void,
): Promise<UnlistenFn> {
  assertTauri("onSyncProgress");
  return listen<SyncProgress>(SYNC_PROGRESS_EVENT, (e) => cb(e.payload));
}

/**
 * Subscribe to the 401 signal. The handler should run the desktop silent
 * refresh and re-invoke `fsSync` with the fresh token. Payload = connectorId.
 */
export function onTokenExpired(
  cb: (connectorId: string) => void,
): Promise<UnlistenFn> {
  assertTauri("onTokenExpired");
  return listen<string>(TOKEN_EXPIRED_EVENT, (e) => cb(e.payload));
}

/** Subscribe to live watcher batches. The handler re-runs `fsSync` for
 * `upserted` and `fsDeleteFile` for `removed`. Returns an unlisten fn. */
export function onWatchEvent(cb: (e: WatchEvent) => void): Promise<UnlistenFn> {
  assertTauri("onWatchEvent");
  return listen<WatchEvent>(WATCH_EVENT, (e) => cb(e.payload));
}

/** Subscribe to the nightly fs-sync tick. The handler enumerates active
 * filesystem connectors and re-runs ``runFilesystemSync`` for each. */
export function onNightlySyncTick(
  cb: (p: NightlySyncTick) => void,
): Promise<UnlistenFn> {
  assertTauri("onNightlySyncTick");
  return listen<NightlySyncTick>(NIGHTLY_SYNC_TICK_EVENT, (e) => cb(e.payload));
}
